import React, { useCallback, useEffect, useState } from "react";
import aiDevelopmentService from "../../services/aiDevelopmentService";

export default function AiAzureDevOpsStorySyncPanel({ storyId, originSystem, t }) {
  const [status, setStatus] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setStatus(await aiDevelopmentService.storyAzureDevOpsStatus(storyId));
    } catch {
      setStatus(null);
    }
  }, [storyId]);

  useEffect(() => {
    load();
  }, [load]);

  if (!status || !status.azureDevOpsUrl) return null;

  const run = async (fn) => {
    setBusy(true);
    setError("");
    try {
      await fn();
      await load();
    } catch (e) {
      setError(e?.message || t("common.errorOccurred"));
    } finally {
      setBusy(false);
    }
  };

  const resolve = (conflictId, resolution) =>
    run(() => aiDevelopmentService.resolveAzureDevOpsConflict(conflictId, { resolution }));

  return (
    <div className="ai-azure-devops-sync-panel">
      <h3>{t("aiDevelopment.azureDevOpsSync.storyTitle")}</h3>
      {error && <div className="ai-studio-alert">{error}</div>}
      <div className="ai-azure-devops-sync-status">
        <span>{t("aiDevelopment.azureDevOpsSync.storyOrigin")}: {t(`aiDevelopment.azureDevOpsSync.origin${originSystem === "AZURE_DEVOPS" ? "AzureDevOps" : "Idax"}`)}</span>
        <a href={status.azureDevOpsUrl} target="_blank" rel="noreferrer">#{status.azureDevOpsWorkItemId}</a>
        <span className="ai-status">{t(`aiDevelopment.azureDevOpsSync.linkStatus${status.linkStatus}`)}</span>
        {status.lastSyncedAt && (
          <span>{t("aiDevelopment.azureDevOpsSync.lastSynced")}: {new Date(status.lastSyncedAt).toLocaleString()}</span>
        )}
        {status.lastError && <span className="ai-status ai-status-error">{status.lastError}</span>}
        {status.filteredReason && <span className="ai-status ai-status-error">{status.filteredReason}</span>}
      </div>
      <div className="ai-actions">
        <button type="button" className="btn btn-default" disabled={busy} onClick={() => run(() => aiDevelopmentService.retryAzureDevOpsSync(storyId))}>
          {t("aiDevelopment.azureDevOpsSync.retry")}
        </button>
        {status.linkStatus === "FILTERED" ? (
          <button type="button" className="btn btn-default" disabled={busy} onClick={() => run(() => aiDevelopmentService.includeInAzureDevOpsSync(storyId))}>
            {t("aiDevelopment.azureDevOpsSync.include")}
          </button>
        ) : (
          <button type="button" className="btn btn-default" disabled={busy} onClick={() => run(() => aiDevelopmentService.excludeFromAzureDevOpsSync(storyId))}>
            {t("aiDevelopment.azureDevOpsSync.exclude")}
          </button>
        )}
      </div>
      {status.pendingConflicts?.length > 0 && (
        <div className="ai-azure-devops-sync-conflicts">
          <h4>{t("aiDevelopment.azureDevOpsSync.conflictsTitle")}</h4>
          {status.pendingConflicts.map((c) => (
            <div className="ai-azure-devops-sync-conflict" key={c.id}>
              <strong>{c.fieldName}</strong>
              <div className="ai-azure-devops-sync-conflict-values">
                <span>{t("aiDevelopment.azureDevOpsSync.idaxValue")}: {c.idaxValue}</span>
                <span>{t("aiDevelopment.azureDevOpsSync.azureDevOpsValue")}: {c.azureDevOpsValue}</span>
              </div>
              <div className="ai-actions">
                <button type="button" className="btn btn-default" onClick={() => resolve(c.id, "RESOLVED_IDAX")}>
                  {t("aiDevelopment.azureDevOpsSync.resolveIdax")}
                </button>
                <button type="button" className="btn btn-default" onClick={() => resolve(c.id, "RESOLVED_EXTERNAL")}>
                  {t("aiDevelopment.azureDevOpsSync.resolveAzureDevOps")}
                </button>
                <button type="button" className="btn btn-default" onClick={() => resolve(c.id, "RESOLVED_MANUAL")}>
                  {t("aiDevelopment.azureDevOpsSync.resolveManual")}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
